<template>
    <div class="prd-page">

        <!-- Back -->
        <div class="prd-back" @click="$router.back()">
            <i class="fas fa-arrow-left"></i> Back to Reports
        </div>

        <!-- Loading -->
        <div v-if="loadingData" class="prd-loading">
            <i class="fas fa-circle-notch fa-spin"></i> Loading project data…
        </div>

        <template v-else>
            <!-- Header -->
            <div class="prd-header">
                <div class="prd-header-left">
                    <h2 class="prd-title">{{ projectName }}</h2>
                    <p class="prd-subtitle">
                        <span v-if="projectDates.start">{{ projectDates.start }} — {{ projectDates.end }}</span>
                    </p>
                </div>
                <div class="prd-header-right">
                    <span class="prd-health-badge" :class="healthClass(health)">{{ health }}</span>
                    <button class="prd-print-btn" @click="printPage">
                        <i class="fas fa-print"></i> Print / PDF
                    </button>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="prd-cards">
                <div class="prd-card">
                    <div class="prd-card-icon" style="background:#eff6ff;color:#3b82f6"><i class="fas fa-tasks"></i></div>
                    <div>
                        <div class="prd-card-val">{{ stats.total }}</div>
                        <div class="prd-card-lbl">Total Tasks</div>
                    </div>
                </div>
                <div class="prd-card">
                    <div class="prd-card-icon" style="background:#dcfce7;color:#15803d"><i class="fas fa-check-circle"></i></div>
                    <div>
                        <div class="prd-card-val">{{ stats.completed }}</div>
                        <div class="prd-card-lbl">Completed</div>
                    </div>
                </div>
                <div class="prd-card">
                    <div class="prd-card-icon" style="background:#dbeafe;color:#1d4ed8"><i class="fas fa-spinner"></i></div>
                    <div>
                        <div class="prd-card-val">{{ stats.inProgress }}</div>
                        <div class="prd-card-lbl">In Progress</div>
                    </div>
                </div>
                <div class="prd-card">
                    <div class="prd-card-icon" style="background:#fefce8;color:#ca8a04"><i class="fas fa-clock"></i></div>
                    <div>
                        <div class="prd-card-val">{{ stats.pending }}</div>
                        <div class="prd-card-lbl">Pending</div>
                    </div>
                </div>
                <div class="prd-card">
                    <div class="prd-card-icon" style="background:#fef2f2;color:#ef4444"><i class="fas fa-exclamation-circle"></i></div>
                    <div>
                        <div class="prd-card-val">{{ stats.overdue }}</div>
                        <div class="prd-card-lbl">Overdue</div>
                    </div>
                </div>
            </div>

            <!-- Progress Bar -->
            <div class="prd-progress-section">
                <div class="prd-progress-block">
                    <div class="prd-progress-label">
                        <span>Task Completion</span>
                        <span class="prd-progress-pct">{{ stats.completionPct }}%</span>
                    </div>
                    <div class="prd-progress-track">
                        <div class="prd-progress-fill prd-fill-green" :style="{ width: stats.completionPct + '%' }"></div>
                    </div>
                </div>
                <div class="prd-progress-block">
                    <div class="prd-progress-label">
                        <span>Timeline Elapsed</span>
                        <span class="prd-progress-pct">{{ stats.timelinePct }}%</span>
                    </div>
                    <div class="prd-progress-track">
                        <div class="prd-progress-fill prd-fill-blue" :style="{ width: stats.timelinePct + '%' }"></div>
                    </div>
                </div>
            </div>

            <!-- Status Breakdown -->
            <div v-if="statusBreakdown.length" class="prd-status-row">
                <span v-for="sb in statusBreakdown" :key="sb.name" class="prd-status-chip" :style="statusPillStyle(sb.name)">
                    {{ sb.name }} <strong>{{ sb.count }}</strong>
                </span>
            </div>

            <!-- Assignee Summary -->
            <div v-if="assigneeSummary.length" class="prd-assignee-section">
                <div class="prd-section-title"><i class="fas fa-users"></i> Team Members</div>
                <div class="prd-assignee-list">
                    <div v-for="a in assigneeSummary" :key="a.name" class="prd-assignee-card">
                        <div class="prd-assignee-avatar">{{ initials(a.name) }}</div>
                        <div>
                            <div class="prd-assignee-name">{{ a.name }}</div>
                            <div class="prd-assignee-stats">{{ a.total }} tasks &bull; {{ a.completed }} done</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tasks Table -->
            <div class="prd-table-section">
                <div class="prd-section-header">
                    <div class="prd-section-title"><i class="fas fa-list-ul"></i> All Tasks</div>
                    <div class="prd-table-controls">
                        <div class="prd-search-wrap">
                            <i class="fas fa-search prd-search-icon"></i>
                            <input v-model="taskSearch" class="prd-search-input" placeholder="Search tasks..." />
                        </div>
                        <select v-model="taskStatusFilter" class="prd-filter-sel">
                            <option value="">All Statuses</option>
                            <option v-for="s in taskStatuses" :key="s" :value="s">{{ s }}</option>
                        </select>
                        <label class="prd-toggle-label">
                            <input type="checkbox" v-model="showSubtasks" class="prd-toggle-input" />
                            Show subtasks
                        </label>
                    </div>
                </div>

                <div class="prd-table-wrap">
                    <table class="prd-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Task Name</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Assigned To</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(task, i) in filteredTasks" :key="task.id"
                                :class="{ 'prd-row-overdue': isOverdueTask(task), 'prd-row-sub': task.taskId }">
                                <td class="prd-num">{{ i + 1 }}</td>
                                <td>
                                    <div class="prd-task-name" :class="{ 'prd-sub-indent': task.taskId }">
                                        <i v-if="task.taskId" class="fas fa-level-up-alt prd-sub-icon"></i>
                                        {{ task.name }}
                                    </div>
                                </td>
                                <td>
                                    <span class="prd-type-chip" :class="task.taskId ? 'prd-chip-sub' : 'prd-chip-task'">
                                        {{ task.taskId ? 'Subtask' : 'Task' }}
                                    </span>
                                </td>
                                <td>
                                    <span class="prd-status-pill" :style="statusPillStyle(task.Status && task.Status.name)">
                                        {{ task.Status ? task.Status.name : '—' }}
                                    </span>
                                </td>
                                <td>{{ getAssignees(task) || '—' }}</td>
                                <td class="prd-date">{{ formatIndianDate(task.startDate) }}</td>
                                <td class="prd-date" :class="{ 'prd-over-date': isOverdueTask(task) }">
                                    {{ formatIndianDate(task.endDate) }}
                                    <span v-if="isOverdueTask(task)" class="prd-over-chip">Overdue</span>
                                </td>
                            </tr>
                            <tr v-if="filteredTasks.length === 0">
                                <td colspan="7" class="prd-empty">
                                    <i class="fas fa-inbox"></i> No tasks found
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { formatIndianDate } from "../../../utils/common"

export default {
    data() {
        return {
            loadingData: false,
            tasks: [],
            taskSearch: "",
            taskStatusFilter: "",
            showSubtasks: true,
        }
    },
    async created() {
        await this.fetchData()
    },
    computed: {
        projectId() {
            return this.$route.params.id
        },
        projectName() {
            return this.tasks[0]?.Project?.name || `Project #${this.projectId}`
        },
        projectDates() {
            const dates = this.tasks.map((t) => ({ s: t.startDate, e: t.endDate })).filter((d) => d.s || d.e)
            if (!dates.length) return {}
            const starts = dates.map((d) => new Date(d.s)).filter((d) => !isNaN(d))
            const ends   = dates.map((d) => new Date(d.e)).filter((d) => !isNaN(d))
            return {
                start: starts.length ? formatIndianDate(new Date(Math.min(...starts)).toISOString()) : null,
                end:   ends.length   ? formatIndianDate(new Date(Math.max(...ends)).toISOString())   : null,
            }
        },
        stats() {
            const today = new Date(); today.setHours(0,0,0,0)
            const total = this.tasks.length
            const completed = this.tasks.filter((t) => this.isCompleted(t)).length
            const inProgress = this.tasks.filter((t) => this.isInProgress(t)).length
            const pending = total - completed - inProgress
            const overdue = this.tasks.filter((t) => this.isOverdueTask(t)).length
            const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0

            const dates = this.tasks.map((t) => ({ s: t.startDate, e: t.endDate })).filter((d) => d.s && d.e)
            let timelinePct = 0
            if (dates.length) {
                const minStart = new Date(Math.min(...dates.map((d) => new Date(d.s))))
                const maxEnd   = new Date(Math.max(...dates.map((d) => new Date(d.e))))
                const totalMs = maxEnd - minStart
                if (totalMs > 0) timelinePct = Math.max(0, Math.min(100, Math.round(((today - minStart) / totalMs) * 100)))
            }
            return { total, completed, inProgress, pending, overdue, completionPct, timelinePct }
        },
        health() {
            const { total, completed, overdue, completionPct, timelinePct } = this.stats
            const today = new Date(); today.setHours(0,0,0,0)
            const maxEnd = this.tasks.map((t) => t.endDate).filter(Boolean).map((d) => new Date(d))
            const projectEnd = maxEnd.length ? new Date(Math.max(...maxEnd)) : null
            const isOverdue = projectEnd && projectEnd < today && completed < total

            if (completed === total && total > 0) return "Done"
            if (overdue > 0 || (isOverdue && completionPct < 80)) return "At Risk"
            if (completionPct < timelinePct - 15) return "Behind"
            return "On Track"
        },
        statusBreakdown() {
            const map = {}
            for (const t of this.tasks) {
                const name = t.Status?.name || "Unknown"
                map[name] = (map[name] || 0) + 1
            }
            return Object.entries(map).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count)
        },
        assigneeSummary() {
            const map = {}
            for (const t of this.tasks) {
                const assignees = [...(t.Reporter || []), ...(t.Assignees || [])]
                const seen = new Set()
                for (const u of assignees) {
                    const name = u.firstName || u.username || "Unknown"
                    if (seen.has(name)) continue
                    seen.add(name)
                    if (!map[name]) map[name] = { name, total: 0, completed: 0 }
                    map[name].total++
                    if (this.isCompleted(t)) map[name].completed++
                }
            }
            return Object.values(map).sort((a, b) => b.total - a.total)
        },
        taskStatuses() {
            const s = new Set(this.tasks.map((t) => t.Status?.name).filter(Boolean))
            return [...s].sort()
        },
        filteredTasks() {
            let list = this.tasks
            if (!this.showSubtasks) list = list.filter((t) => !t.taskId)
            if (this.taskSearch.trim()) {
                const q = this.taskSearch.trim().toLowerCase()
                list = list.filter((t) => t.name?.toLowerCase().includes(q))
            }
            if (this.taskStatusFilter) {
                list = list.filter((t) => t.Status?.name === this.taskStatusFilter)
            }
            return list
        },
    },
    methods: {
        formatIndianDate,
        async fetchData() {
            try {
                this.loadingData = true
                const { data } = await http.get("/project-tasks")
                const all = data.result || []
                const id = String(this.projectId)
                this.tasks = all.filter((t) => t.Project && String(t.Project.id) === id)
            } catch (err) {
                console.error("Error fetching project detail:", err)
            } finally {
                this.loadingData = false
            }
        },
        isCompleted(task) {
            const s = (task.Status?.name || "").toLowerCase()
            return s.includes("complet") || s === "done" || s === "closed"
        },
        isInProgress(task) {
            const s = (task.Status?.name || "").toLowerCase()
            return s.includes("progress") || s.includes("ongoing") || s.includes("active") || s.includes("review")
        },
        isOverdueTask(task) {
            if (!task.endDate || this.isCompleted(task)) return false
            const today = new Date(); today.setHours(0,0,0,0)
            return new Date(task.endDate) < today
        },
        getAssignees(task) {
            const src = [...(task.Reporter || []), ...(task.Assignees || [])]
            return [...new Set(src.map((u) => u.firstName || u.username || "").filter(Boolean))].join(", ")
        },
        healthClass(h) {
            if (h === "Done")     return "prd-health-done"
            if (h === "On Track") return "prd-health-ok"
            if (h === "Behind")   return "prd-health-behind"
            return "prd-health-risk"
        },
        statusPillStyle(name) {
            if (!name) return {}
            const n = name.toLowerCase()
            if (n.includes("complet") || n === "done" || n === "closed")
                return { background: "#dcfce7", color: "#15803d" }
            if (n.includes("progress") || n.includes("active") || n.includes("ongoing"))
                return { background: "#dbeafe", color: "#1d4ed8" }
            if (n.includes("block") || n.includes("hold"))
                return { background: "#fee2e2", color: "#b91c1c" }
            return { background: "#f1f5f9", color: "#475569" }
        },
        initials(name) {
            return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
        },
        printPage() { window.print() },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.prd-page { padding: 20px; max-width: 1300px; }

.prd-back {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: 13px; color: $gray500; cursor: pointer; margin-bottom: 20px;
    transition: color 0.15s;
    &:hover { color: $primaryColor; }
    i { font-size: 12px; }
}

.prd-loading { padding: 60px; text-align: center; color: $gray400; font-size: 15px; i { margin-right: 8px; } }

.prd-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    flex-wrap: wrap; gap: 12px; margin-bottom: 24px;
}
.prd-title { font-size: 20px; font-weight: 700; color: $gray800; margin: 0; }
.prd-subtitle { font-size: 13px; color: $gray500; margin: 4px 0 0; }
.prd-header-right { display: flex; align-items: center; gap: 10px; }

.prd-health-badge {
    display: inline-block; padding: 5px 14px; border-radius: $radiusFull;
    font-size: 12px; font-weight: 600;
}
.prd-health-done   { background: #dcfce7; color: #15803d; }
.prd-health-ok     { background: #dbeafe; color: #1d4ed8; }
.prd-health-behind { background: #fef9c3; color: #b45309; }
.prd-health-risk   { background: #fee2e2; color: #dc2626; }

.prd-print-btn {
    display: flex; align-items: center; gap: 6px; padding: 8px 14px;
    background: $primaryColor; color: white; border: none; border-radius: $radiusSm;
    font-size: 13px; font-weight: 500; cursor: pointer; transition: opacity 0.15s;
    &:hover { opacity: 0.88; }
}

.prd-cards {
    display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; margin-bottom: 20px;
}
@media (max-width: 900px) { .prd-cards { grid-template-columns: repeat(2, 1fr); } }
.prd-card {
    background: white; border: 1px solid $gray200; border-radius: $radiusMd;
    padding: 16px; display: flex; align-items: center; gap: 14px; box-shadow: $shadowSm;
}
.prd-card-icon { width: 42px; height: 42px; border-radius: $radiusSm; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.prd-card-val  { font-size: 22px; font-weight: 700; color: $gray800; line-height: 1; }
.prd-card-lbl  { font-size: 12px; color: $gray500; margin-top: 3px; }

.prd-progress-section {
    background: white; border: 1px solid $gray200; border-radius: $radiusMd;
    padding: 18px 20px; margin-bottom: 16px; box-shadow: $shadowSm;
    display: flex; flex-direction: column; gap: 14px;
}
.prd-progress-block {}
.prd-progress-label {
    display: flex; justify-content: space-between; align-items: center;
    font-size: 13px; color: $gray600; font-weight: 500; margin-bottom: 6px;
}
.prd-progress-pct { font-size: 13px; font-weight: 700; color: $gray800; }
.prd-progress-track { height: 8px; background: $gray100; border-radius: 999px; overflow: hidden; }
.prd-progress-fill { height: 100%; border-radius: 999px; transition: width 0.5s ease; }
.prd-fill-green { background: #22c55e; }
.prd-fill-blue  { background: #3b82f6; }

.prd-status-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
.prd-status-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 12px; border-radius: $radiusFull; font-size: 12px; font-weight: 500;
    strong { font-weight: 700; }
}

.prd-section-title {
    font-size: 14px; font-weight: 700; color: $gray700; margin-bottom: 12px;
    display: flex; align-items: center; gap: 8px;
    i { color: $primaryColor; }
}
.prd-assignee-section {
    background: white; border: 1px solid $gray200; border-radius: $radiusMd;
    padding: 18px 20px; margin-bottom: 20px; box-shadow: $shadowSm;
}
.prd-assignee-list { display: flex; flex-wrap: wrap; gap: 12px; }
.prd-assignee-card {
    display: flex; align-items: center; gap: 10px;
    background: $gray50; border: 1px solid $gray200; border-radius: $radiusMd;
    padding: 10px 14px;
}
.prd-assignee-avatar {
    width: 34px; height: 34px; border-radius: 50%;
    background: $primaryColor; color: white;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; flex-shrink: 0;
}
.prd-assignee-name  { font-size: 13px; font-weight: 600; color: $gray800; }
.prd-assignee-stats { font-size: 11px; color: $gray500; margin-top: 1px; }

.prd-table-section {
    background: white; border: 1px solid $gray200; border-radius: $radiusMd;
    overflow: hidden; box-shadow: $shadowSm;
}
.prd-section-header {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 10px; padding: 16px 20px; border-bottom: 1px solid $gray100;
}
.prd-table-controls { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.prd-search-wrap { position: relative; display: flex; align-items: center; }
.prd-search-icon { position: absolute; left: 10px; color: $gray400; font-size: 12px; pointer-events: none; }
.prd-search-input {
    padding: 7px 10px 7px 30px; border: 1px solid $gray200; border-radius: $radiusSm;
    font-size: 13px; width: 180px; outline: none;
    &:focus { border-color: $primaryColor; }
}
.prd-filter-sel {
    padding: 7px 10px; border: 1px solid $gray200; border-radius: $radiusSm;
    font-size: 13px; background: white; color: $gray700; outline: none; cursor: pointer;
}
.prd-toggle-label {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; color: $gray600; cursor: pointer; user-select: none;
}
.prd-toggle-input { cursor: pointer; }

.prd-table-wrap { overflow-x: auto; }
.prd-table {
    width: 100%; border-collapse: collapse; font-size: 13px;
    thead tr { background: $gray50; border-bottom: 1px solid $gray200; }
    th {
        padding: 10px 14px; text-align: left; font-weight: 600; font-size: 12px;
        color: $gray500; text-transform: uppercase; letter-spacing: 0.03em; white-space: nowrap;
    }
    td { padding: 10px 14px; border-bottom: 1px solid $gray100; vertical-align: middle; color: $gray700; }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: $gray50; }
}
.prd-row-overdue td { background: #fff8f8 !important; }
.prd-row-sub td { background: #fafbff; }

.prd-num { color: $gray400; font-size: 12px; }
.prd-task-name { font-weight: 500; color: $gray800; }
.prd-sub-indent { padding-left: 18px; display: flex; align-items: center; gap: 6px; }
.prd-sub-icon { color: $gray400; font-size: 11px; transform: rotate(90deg); }

.prd-type-chip { display: inline-block; padding: 2px 7px; border-radius: $radiusFull; font-size: 10px; font-weight: 600; }
.prd-chip-task { background: #dbeafe; color: #1d4ed8; }
.prd-chip-sub  { background: #f3e8ff; color: #7c3aed; }

.prd-status-pill { display: inline-block; padding: 3px 10px; border-radius: $radiusFull; font-size: 11px; font-weight: 500; white-space: nowrap; }
.prd-date       { font-size: 12px; color: $gray600; white-space: nowrap; }
.prd-over-date  { color: #dc2626 !important; font-weight: 600; }
.prd-over-chip  {
    display: inline-block; margin-left: 5px; padding: 1px 6px;
    background: #fee2e2; color: #dc2626; border-radius: $radiusFull;
    font-size: 10px; font-weight: 600;
}

.prd-empty { text-align: center; padding: 40px; color: $gray400; i { display: block; font-size: 24px; margin-bottom: 8px; } }
</style>

<style>
@media print {
    body * { visibility: hidden !important; }
    .prd-page, .prd-page * { visibility: visible !important; }
    .prd-page { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
    .prd-back, .prd-print-btn, .prd-table-controls { display: none !important; }
    .prd-progress-fill, .prd-health-badge, .prd-status-pill, .prd-type-chip { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page { size: A4 landscape; margin: 15mm; }
}
</style>
